/**
 * Utility functions for CSV import/export of categories
 */

export interface CategoryCSVRow {
  name: string;
  type: string;
  description: string;
  requirements: string;
  subcategories: string;
  verificationRequired: string;
  emergencyService: string;
  // Translations for category name
  translatedName_ar?: string;
  translatedName_cs?: string;
  translatedName_de?: string;
  translatedName_en?: string;
  translatedName_es?: string;
  translatedName_fr?: string;
  translatedName_hi?: string;
  translatedName_it?: string;
  translatedName_ja?: string;
  translatedName_ko?: string;
  translatedName_nl?: string;
  translatedName_pt?: string;
  translatedName_ru?: string;
  translatedName_zh?: string;
  // Translations for subcategories (pipe-separated subcategories with comma-separated translations)
  translatedSubCategories_ar?: string;
  translatedSubCategories_cs?: string;
  translatedSubCategories_de?: string;
  translatedSubCategories_en?: string;
  translatedSubCategories_es?: string;
  translatedSubCategories_fr?: string;
  translatedSubCategories_hi?: string;
  translatedSubCategories_it?: string;
  translatedSubCategories_ja?: string;
  translatedSubCategories_ko?: string;
  translatedSubCategories_nl?: string;
  translatedSubCategories_pt?: string;
  translatedSubCategories_ru?: string;
  translatedSubCategories_zh?: string;
}

export const SUPPORTED_LANGUAGES = [
  "ar", "cs", "de", "en", "es", "fr", "hi", "it", "ja", "ko", "nl", "pt", "ru", "zh"
];

export const CSV_HEADERS = [
  "name",
  "type",
  "description",
  "requirements",
  "subcategories",
  "verificationRequired",
  "emergencyService",
  // Translation headers for category name
  ...SUPPORTED_LANGUAGES.map(lang => `translatedName_${lang}`),
  // Translation headers for subcategories
  ...SUPPORTED_LANGUAGES.map(lang => `translatedSubCategories_${lang}`),
];

/**
 * Generate CSV template with example data
 */
export const generateCSVTemplate = (): string => {
  const headers = CSV_HEADERS.join(",");
  
  // Example row with instructions
  const exampleRow = [
    "Plumbing",
    "1",
    "Professional plumbing services",
    "License|Insurance|Experience",
    "Pipe Repair|Installation|Emergency",
    "true",
    "false",
    // Example translations for name
    ...SUPPORTED_LANGUAGES.map(() => ""),
    // Example translations for subcategories (matching original subcategories order)
    ...SUPPORTED_LANGUAGES.map(() => ""),
  ].map(escapeCSVValue).join(",");

  const instructionRow = [
    "Enter category name",
    "1=Home, 2=Professional, 3=International",
    "Enter description",
    "Separate with |",
    "Separate with |",
    "true/false",
    "true/false",
    ...SUPPORTED_LANGUAGES.map(lang => `Translation in ${lang}`),
    ...SUPPORTED_LANGUAGES.map(lang => `Subcategory translations in ${lang} (pipe-separated, matching order)`),
  ].map(escapeCSVValue).join(",");

  return `${headers}\n${exampleRow}\n${instructionRow}`;
};

/**
 * Export categories to CSV
 */
export const exportCategoriesToCSV = (categories: any[]): string => {
  const headers = CSV_HEADERS.join(",");
  
  const rows = categories.map(category => {
    const row = [
      category.name,
      category.type?.id || "1",
      category.description || "",
      (category.requirements || []).join("|"),
      (category.subcategories || []).join("|"),
      category.verificationRequired ? "true" : "false",
      category.emergencyService ? "true" : "false",
      // Category name translations
      ...SUPPORTED_LANGUAGES.map(lang => 
        category.translatedName?.[lang] || ""
      ),
      // Subcategory translations
      ...SUPPORTED_LANGUAGES.map(lang => {
        const translations = category.translatedSubCategories?.[lang] || {};
        // Get translations in the same order as original subcategories
        return (category.subcategories || [])
          .map((sub: string) => translations[sub] || "")
          .join("|");
      }),
    ];
    
    return row.map(escapeCSVValue).join(",");
  });

  return `${headers}\n${rows.join("\n")}`;
};

/**
 * Parse CSV content to category data
 */
export const parseCSVToCategories = (csvContent: string): {
  categories: any[];
  errors: string[];
} => {
  const errors: string[] = [];
  const categories: any[] = [];
  
  const lines = csvContent.split("\n").filter(line => line.trim());
  
  if (lines.length < 2) {
    errors.push("CSV file is empty or has no data rows");
    return { categories, errors };
  }

  const headers = parseCSVLine(lines[0]);
  
  // Validate headers
  const requiredHeaders = ["name", "type", "description"];
  const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
  if (missingHeaders.length > 0) {
    errors.push(`Missing required headers: ${missingHeaders.join(", ")}`);
    return { categories, errors };
  }

  // Parse data rows (skip header)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    try {
      const values = parseCSVLine(line);
      const rowData: any = {};
      
      headers.forEach((header, index) => {
        rowData[header] = values[index] || "";
      });

      // Validate required fields
      if (!rowData.name?.trim()) {
        errors.push(`Row ${i + 1}: Name is required`);
        continue;
      }

      if (!rowData.type?.trim()) {
        errors.push(`Row ${i + 1}: Type is required`);
        continue;
      }

      // Parse type
      const typeId = rowData.type.trim();
      if (!["1", "2", "3"].includes(typeId)) {
        errors.push(`Row ${i + 1}: Type must be 1, 2, or 3`);
        continue;
      }

      // Build category object
      const category: any = {
        name: rowData.name.trim(),
        type: { id: typeId },
        description: rowData.description?.trim() || "",
        requirements: rowData.requirements 
          ? rowData.requirements.split("|").map((r: string) => r.trim()).filter(Boolean)
          : [],
        subcategories: rowData.subcategories
          ? rowData.subcategories.split("|").map((s: string) => s.trim()).filter(Boolean)
          : [],
        verificationRequired: rowData.verificationRequired?.toLowerCase() === "true",
        emergencyService: rowData.emergencyService?.toLowerCase() === "true",
        translatedName: {},
        translatedSubCategories: {},
      };

      // Parse translated names
      SUPPORTED_LANGUAGES.forEach(lang => {
        const translatedName = rowData[`translatedName_${lang}`]?.trim();
        if (translatedName) {
          category.translatedName[lang] = translatedName;
        }
      });

      // Parse translated subcategories
      SUPPORTED_LANGUAGES.forEach(lang => {
        const translatedSubCats = rowData[`translatedSubCategories_${lang}`]?.trim();
        if (translatedSubCats && category.subcategories.length > 0) {
          const translations = translatedSubCats.split("|").map((t: string) => t.trim());
          const translationMap: { [key: string]: string } = {};
          
          // Map translations to original subcategories by index
          category.subcategories.forEach((subcat: string, index: number) => {
            if (translations[index]) {
              translationMap[subcat] = translations[index];
            }
          });
          
          if (Object.keys(translationMap).length > 0) {
            category.translatedSubCategories[lang] = translationMap;
          }
        }
      });

      categories.push(category);
    } catch (error: any) {
      errors.push(`Row ${i + 1}: ${error.message}`);
    }
  }

  return { categories, errors };
};

/**
 * Parse a single CSV line handling quoted values
 */
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === "\"") {
      if (inQuotes && nextChar === "\"") {
        // Escaped quote
        current += "\"";
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      // End of field
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  
  // Add last field
  result.push(current);
  
  return result;
};

/**
 * Escape CSV value (handle quotes and commas)
 */
const escapeCSVValue = (value: any): string => {
  if (value === null || value === undefined) return "";
  
  const str = String(value);
  
  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
    return `"${str.replace(/"/g, "\"\"")}"`;
  }
  
  return str;
};

/**
 * Download CSV file
 */
export const downloadCSV = (content: string, filename: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

